import { useState, useCallback, useRef, useEffect } from 'react';
import { invokeWithAbort } from '@/utils/invokeWithAbort';
import { isRetryableReportsPostResponse } from '@/lib/reportsPostRetry';

const NERV_RETRY_DELAYS_MS = [2000, 5000, 10000];
const BACKFILL_WINDOW_DAYS = 30;

// Missing dates older than the backfill window are never filled, so polling
// waits only for the ones nerv-data-v6 reports as pending.
function pendingBackfillCount(
  data: { pendingBackfillDatesCount?: number; missingFinancialDatesCount?: number } | null | undefined,
): number {
  return Number(data?.pendingBackfillDatesCount ?? data?.missingFinancialDatesCount ?? 0);
}

interface ProgressiveNervData {
  data: any[];
  loading: boolean;
  backfilling: boolean;
  error: string | null;
  totalCount: number;
  tableNotFound: boolean;
  emptyReason: string | null;
}

interface UploadedInventory {
  sku: string;
  quantity: number;
}

interface UseProgressiveNervOptions {
  organisation: any;
  sources: string[]; // UI filter for financial/price data
  /**
   * FIG-409 · UC-234: send every entry of `sources` as `filters.dataSources`
   * (the Daily NERV Trends chart's saved set). Off by default, so callers such
   * as What-If keep sending only the single `dataSource` they send today.
   */
  multiSource?: boolean;
  dateRange: { startDate: Date; endDate: Date };
  selectedSkus: string[];
  uploadedInventory?: UploadedInventory[] | null;
}

export const useProgressiveNerv = ({
  organisation,
  sources,
  multiSource = false,
  dateRange,
  selectedSkus,
  uploadedInventory
}: UseProgressiveNervOptions) => {
  const [nervState, setNervState] = useState<ProgressiveNervData>({
    data: [],
    loading: false,
    backfilling: false,
    error: null,
    totalCount: 0,
    tableNotFound: false,
    emptyReason: null
  });

  // Cache for storing fetched data
  const cacheRef = useRef<{ key: string, data: any[] } | null>(null);
  const lastFetchKeyRef = useRef<string | null>(null);
  const activePollingKeyRef = useRef<string | null>(null);
  // AbortController to cancel in-flight requests on unmount / filter change
  const abortRef = useRef<AbortController | null>(null);

  // Generate cache key based on filters
  const getCacheKey = useCallback(() => {
    return JSON.stringify({
      orgId: organisation?.id,
      sources: [...sources].sort(),
      multiSource,
      startDate: dateRange.startDate.toISOString(),
      endDate: dateRange.endDate.toISOString(),
      skus: [...selectedSkus].sort(),
      hasUpload: !!uploadedInventory,
      uploadCount: uploadedInventory?.length || 0
    });
  }, [organisation, sources, multiSource, dateRange, selectedSkus, uploadedInventory]);

  const fetchNervData = useCallback(async () => {
    // Retry invokeWithAbort on retryable errors (IDLE_TIMEOUT, 503, 504) with backoff.
    async function fetchWithRetry(
      functionName: string,
      body: Record<string, unknown>,
      signal: AbortSignal
    ) {
      let lastError: unknown;
      for (let attempt = 0; attempt <= NERV_RETRY_DELAYS_MS.length; attempt++) {
        if (signal.aborted) return { data: null, error: { name: 'AbortError' } };
        const result = await invokeWithAbort(functionName, body, signal);
        if (!result.error) return result;
        const err = result.error as any;
        if (err.name === 'AbortError') return result;
        if (!isRetryableReportsPostResponse(err.status ?? 0, err.parsedBody)) return result;
        lastError = result.error;
        if (attempt === NERV_RETRY_DELAYS_MS.length) break;
        await new Promise<void>(resolve => {
          const t = setTimeout(resolve, NERV_RETRY_DELAYS_MS[attempt]);
          signal.addEventListener('abort', () => { clearTimeout(t); resolve(); }, { once: true });
        });
      }
      return { data: null, error: lastError };
    }

    if (!organisation?.id) {
      setNervState({
        data: [],
        loading: false,
        backfilling: false,
        error: 'No organisation data',
        totalCount: 0,
        tableNotFound: false,
        emptyReason: null
      });
      return;
    }

    const cacheKey = getCacheKey();

    // Check cache first
    if (cacheRef.current?.key === cacheKey) {
      return;
    }

    // Prevent duplicate fetches
    if (lastFetchKeyRef.current === cacheKey) {
      return;
    }

    lastFetchKeyRef.current = cacheKey;
    // Abort any previous in-flight request
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setNervState(prev => ({ ...prev, loading: true, error: null }));

    try {
      // Format dates for query
      const startDate = dateRange.startDate.toISOString().split('T')[0];
      const endDate = dateRange.endDate.toISOString().split('T')[0];

      // Check if date range exceeds 7 days — trigger on-demand backfill for the trailing 30d tail.
      // Capping at 30 days avoids dispatching hundreds of workers for year-long ranges while still
      // keeping recent data fresh (covers typical refund/adjustment windows).
      const diffDays = Math.ceil(
        (dateRange.endDate.getTime() - dateRange.startDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      const isExtendedRange = diffDays > 7;

      if (isExtendedRange) {
        setNervState(prev => ({ ...prev, backfilling: true }));
        const backfillStart = new Date(`${endDate}T00:00:00Z`);
        backfillStart.setUTCDate(backfillStart.getUTCDate() - (BACKFILL_WINDOW_DAYS - 1));
        const backfillStartDate = backfillStart.toISOString().split('T')[0];
        // Kick off a backfill coordinator run for longer ranges.
        // This is intentionally non-blocking so NERV data can render immediately.
        void invokeWithAbort('snapshot-recalc-coordinator', {
          organisationId: organisation.id,
          startDate: backfillStartDate > startDate ? backfillStartDate : startDate,
          endDate
        }, controller.signal).catch((coordinatorError: unknown) => {
          console.warn('Failed to invoke snapshot backfill coordinator:', coordinatorError);
        });
      }

      // Determine dataSource for financial/price filtering (UI dropdown).
      // FIG-409: read from a sorted copy. getCacheKey used to sort the `sources`
      // prop in place before this line, so sources[0] was the first in sorted
      // order; keep that exactly (What-If "All" relies on it, WORK_PLAN §7 F2)
      // without mutating the caller's array.
      const orderedSources = [...sources].sort();
      const dataSource = orderedSources.length === 0 || orderedSources.includes('All')
        ? 'All'
        : orderedSources[0];

      // Get inventory source from org config (always uses this for inventory)
      const inventorySourceConfig = organisation?.inventory_source_config?.primary_source;
      const inventorySource = inventorySourceConfig && inventorySourceConfig !== 'all'
        ? inventorySourceConfig
        : null; // null means use all sources for inventory

      // Only multi-source callers (the Daily NERV Trends chart) need nerv-data-v7.
      // Everyone else (Inventory What-If) stays on nerv-data-v6, so a frontend
      // that goes live before v7 is deployed can only affect the Overview chart
      // (deploy-vercel and deploy-edge-functions run in parallel; Cursor review on #360).
      const nervFunction = multiSource ? 'nerv-data-v7' : 'nerv-data-v6';

      const nervQueryParams = {
        organisationId: organisation.id,
        filters: {
          startDate,
          endDate,
          skus: selectedSkus,
          dataSource,       // UI filter - for financial/avg price
          inventorySource,  // Org config - for inventory only
          // FIG-409 · UC-234: the full saved set ([] = All Sources); v7 prefers it over dataSource.
          ...(multiSource ? { dataSources: orderedSources.filter((s) => s !== 'All') } : {}),
        }
      };

      // For extended ranges, poll until backfill produces data for the full range
      let nervData: any;
      let nervError: any;

      // Quick-convert raw DB rows into the shape DailyNervGraph expects.
      // Used for intermediate state updates so the graph renders while polling.
      const toPartialResults = (rows: any[]) =>
        rows
          .map((r: any) => ({
            date: r.date as string,
            current: Number(r.avg_price || 0) === 0 && Number(r.nerv || 0) === 0
              ? null
              : Number(r.nerv || 0),
            uploadedRealistic: 0,
            uploadedPotential: 0,
            combinedRealistic: 0,
            combinedPotential: 0,
            totalInventory: Number(r.inventory_level || 0),
            rawInventoryLevel: Number(r.raw_inventory_level || 0),
            totalUnitsSold: Number(r.total_quantity_sold || 0),
            unitsUsed: Number(r.units_used || 0),
            avgPrice: Number(r.avg_price || 0),
            avgPriceCount: Number(r.avg_price || 0) > 0 ? 1 : 0,
            nev: Number(r.nev || 0),
          }))
          .sort((a, b) => a.date.localeCompare(b.date));

      const pollUntilComplete = async (
        maxPolls: number,
        intervalMs: number,
        initialMissingCount: number,
      ): Promise<{ latestData: any; remainingMissing: number; completed: boolean }> => {
        let latestData = nervData;
        let remainingMissing = initialMissingCount;

        for (let i = 0; i < maxPolls; i++) {
          if (controller.signal.aborted) break;
          if (activePollingKeyRef.current !== cacheKey) break;

          await new Promise(resolve => setTimeout(resolve, intervalMs));
          if (controller.signal.aborted) break;
          if (activePollingKeyRef.current !== cacheKey) break;

          const poll = await fetchWithRetry(nervFunction, nervQueryParams, controller.signal);
          if (poll.error || !poll.data?.success) {
            continue;
          }

          latestData = poll.data;
          remainingMissing = pendingBackfillCount(poll.data);

          if (poll.data.data?.length > 0) {
            setNervState(prev => ({
              ...prev,
              data: toPartialResults(poll.data.data),
            }));
          }

          if (remainingMissing === 0) {
            return { latestData, remainingMissing, completed: true };
          }
        }

        return { latestData, remainingMissing, completed: remainingMissing === 0 };
      };

      if (isExtendedRange) {
        activePollingKeyRef.current = cacheKey;
        // Initial fetch to show whatever data exists immediately
        const initial = await fetchWithRetry(nervFunction, nervQueryParams, controller.signal);
        nervData = initial.data;
        nervError = initial.error;

        // Render partial data straight away so the chart doesn't stay blank while polling
        if (!nervError && nervData?.success && nervData.data?.length > 0) {
          setNervState(prev => ({
            ...prev,
            loading: false,
            data: toPartialResults(nervData.data),
          }));
        }

        // If initial data doesn't cover the full range, check whether historical dates
        // are simply missing (never computed) and trigger a full-range backfill if so.
        if (!nervError && nervData?.success) {
          const missingFinancialDatesCount = pendingBackfillCount(nervData);
          const hasPendingFinancialGaps = missingFinancialDatesCount > 0;

          if (hasPendingFinancialGaps) {
            // Phase 1: fast polling for initial responsiveness.
            const isHistoricalGap = diffDays > 30;
            const fastPollResult = await pollUntilComplete(
              isHistoricalGap ? 36 : 12,
              5000,
              missingFinancialDatesCount,
            );
            nervData = fastPollResult.latestData;

            // Phase 2: keep auto-refreshing in the background at a slower cadence.
            if (
              !fastPollResult.completed &&
              !controller.signal.aborted &&
              activePollingKeyRef.current === cacheKey
            ) {
              const slowPollResult = await pollUntilComplete(
                120, // up to ~1 hour at 30s
                30000,
                fastPollResult.remainingMissing,
              );
              nervData = slowPollResult.latestData;
            }
          }
        }
      } else {
        const result = await fetchWithRetry(nervFunction, nervQueryParams, controller.signal);
        nervData = result.data;
        nervError = result.error;
      }

      if (nervError) {
        if (controller.signal.aborted) return;
        const body = (nervError as any).parsedBody as Record<string, unknown> | null;
        const bodyMsg = typeof body?.error === 'string' ? body.error
          : typeof body?.message === 'string' ? body.message
          : '';
        const actualErrorMessage = bodyMsg || nervError.message || '';

        if (actualErrorMessage.includes('does not exist') || actualErrorMessage.includes('relation')) {
          setNervState({
            data: [],
            loading: false,
            backfilling: false,
            error: null,
            totalCount: 0,
            tableNotFound: true,
            emptyReason: null
          });
          return;
        }
        throw nervError;
      }

      // Bail out if aborted during the async call
      if (controller.signal.aborted) return;

      if (!nervData?.success) {
        const errorMessage = nervData?.error || 'Failed to fetch NERV data';

        // Check if error indicates table doesn't exist
        if (errorMessage.includes('does not exist')) {
          setNervState({
            data: [],
            loading: false,
            backfilling: false,
            error: null,
            totalCount: 0,
            tableNotFound: true,
            emptyReason: null
          });
          return;
        }

        throw new Error(errorMessage);
      }

      let nervResults = nervData.data || [];

      // Aggregate current NERV data by date with realistic and potential uploaded values
      const aggregatedData = new Map<string, {
        date: string;
        current: number;
        uploadedRealistic: number;
        uploadedPotential: number;
        combinedRealistic: number;
        combinedPotential: number;
        // Breakdown details for tooltip
        totalInventory: number;
        // Pre-adjustment inventory used to distinguish 'no snapshot' (hide)
        // from 'snapshot adjusted to 0' (show as $0 point).
        rawInventoryLevel: number;
        totalUnitsSold: number;
        unitsUsed: number;  // MIN(total_inventory, total_sold) at aggregate level
        avgPrice: number;
        avgPriceCount: number;
        nev: number;
      }>();

      // Process current NERV data - sum all SKUs for each date
      nervResults.forEach((row: any) => {
        const existing = aggregatedData.get(row.date) || {
          date: row.date,
          current: 0,
          uploadedRealistic: 0,
          uploadedPotential: 0,
          combinedRealistic: 0,
          combinedPotential: 0,
          totalInventory: 0,
          rawInventoryLevel: 0,
          totalUnitsSold: 0,
          unitsUsed: 0,
          avgPrice: 0,
          avgPriceCount: 0,
          nev: 0
        };
        existing.current += Number(row.nerv || 0);
        existing.totalInventory += Number(row.inventory_level || 0);
        existing.rawInventoryLevel += Number(row.raw_inventory_level || 0);
        existing.totalUnitsSold += Number(row.total_quantity_sold || 0);
        existing.unitsUsed += Number(row.units_used || 0);
        existing.nev += Number(row.nev || 0);

        // Calculate weighted average price
        if (row.avg_price) {
          existing.avgPriceCount += 1;
          existing.avgPrice = ((existing.avgPrice * (existing.avgPriceCount - 1)) + Number(row.avg_price)) / existing.avgPriceCount;
        }

        aggregatedData.set(row.date, existing);
      });

      // If uploaded inventory exists, calculate hypothetical NERV
      if (uploadedInventory && uploadedInventory.length > 0) {
        const { data: hypotheticalData, error: hypotheticalError } = await invokeWithAbort('calculate-hypothetical-nerv', {
          organisationId: organisation.id,
          uploadedInventory,
          filters: {
            startDate,
            endDate,
            dataSource  // UI filter for price source
          }
        }, controller.signal);

        if (hypotheticalError) {
          console.error('Error calculating hypothetical NERV:', hypotheticalError);
        } else if (hypotheticalData?.success && hypotheticalData.uploadedNervByDate) {
          // Sum up uploaded NERV across all SKUs for each date - both realistic and potential
          hypotheticalData.uploadedNervByDate.forEach((dateEntry: any) => {
            const existing = aggregatedData.get(dateEntry.date) || {
              date: dateEntry.date,
              current: 0,
              uploadedRealistic: 0,
              uploadedPotential: 0,
              combinedRealistic: 0,
              combinedPotential: 0,
              totalInventory: 0,
              rawInventoryLevel: 0,
              totalUnitsSold: 0,
              unitsUsed: 0,
              avgPrice: 0,
              avgPriceCount: 0,
              nev: 0
            };
            existing.uploadedRealistic += Number(dateEntry.realisticNerv || 0);
            existing.uploadedPotential += Number(dateEntry.potentialNerv || 0);
            existing.combinedRealistic = existing.current + existing.uploadedRealistic;
            existing.combinedPotential = existing.current + existing.uploadedPotential;
            aggregatedData.set(dateEntry.date, existing);
          });
        }
      }

      // Convert map to sorted array, filtering out days with no valid NERV data
      // (days where financial data is missing - avg_price = 0 indicates no financial snapshot)
      nervResults = Array.from(aggregatedData.values())
        .map(entry => {
          // If no financial data for this day (avg_price = 0), set current to null
          // so the chart draws a straight line through the gap
          if (entry.avgPrice === 0 && entry.current === 0) {
            return { ...entry, current: null };
          }
          return entry;
        })
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      // Hide days that never had an inventory snapshot row (true sync gaps) so they
      // don't create visible drops. Days where a snapshot existed but adjustments
      // brought it to 0 are kept so the user can see legitimate zero-stock periods.
      nervResults = nervResults.filter((entry) => entry.rawInventoryLevel !== 0);

      // Bail out if aborted during processing
      if (controller.signal.aborted) return;

      // Cache the results
      cacheRef.current = { key: cacheKey, data: nervResults };

      setNervState({
        data: nervResults,
        loading: false,
        backfilling: isExtendedRange && pendingBackfillCount(nervData) > 0,
        error: null,
        totalCount: nervResults.length,
        tableNotFound: false,
        emptyReason: nervData?.reason || null
      });

    } catch (error: any) {
      setNervState(prev => ({ ...prev, backfilling: false }));
      if (controller.signal.aborted) return;
      // Silently ignore AbortError
      if (error?.name === 'AbortError') return;
      console.error('Error fetching NERV data:', error);

      const parsed = error.parsedBody as Record<string, unknown> | null | undefined;
      const parsedMsg = typeof parsed?.error === 'string' ? parsed.error
        : typeof parsed?.message === 'string' ? parsed.message
        : '';
      const code = typeof parsed?.code === 'string' ? parsed.code : '';
      const errorMessage = code === 'IDLE_TIMEOUT'
        ? 'The request timed out. Please try again.'
        : parsedMsg || error.message || 'Something went wrong loading NERV data.';

      // Check if this is a "table doesn't exist" error - should hide the card
      if (errorMessage.includes('does not exist')) {
        setNervState({
          data: [],
          loading: false,
          backfilling: false,
          error: null,
          totalCount: 0,
          tableNotFound: true,
          emptyReason: null
        });
        return;
      }

      setNervState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }));
    } finally {
      setNervState(prev => ({ ...prev, backfilling: false }));
      if (activePollingKeyRef.current === cacheKey) {
        activePollingKeyRef.current = null;
      }
      lastFetchKeyRef.current = null;
    }
  }, [organisation, sources, multiSource, dateRange, selectedSkus, uploadedInventory, getCacheKey]);

  // Fetch data when dependencies change; abort on unmount / re-run
  useEffect(() => {
    fetchNervData();
    return () => {
      activePollingKeyRef.current = null;
      abortRef.current?.abort();
    };
  }, [fetchNervData]);

  return {
    data: nervState.data,
    isLoading: nervState.loading,
    backfilling: nervState.backfilling,
    error: nervState.error,
    totalCount: nervState.totalCount,
    tableNotFound: nervState.tableNotFound,
    emptyReason: nervState.emptyReason,
    refetch: fetchNervData
  };
};
